import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { Card, Col, Row, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import { RootState } from '../store/rootReducer';
import QuestionnaireDetailCategoryRow from '../components/questionnaires/QuestionnaireDetailCategoryRow';
import { QuestionnaireStatusEnum } from '../api/openapi/api';
import { fetchApplicationQuestionnaire, finishQuestionnaireAnswers, submitQuestionnaireAnswers, saveQuestionnaireAnswers } from '../store/questionnaireSlice';
import { AppDispatch } from '../store/store';

const QuestionnaireDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['questionnaireDetail', 'common']);
  const history = useHistory();

  const dispatch = useDispatch<AppDispatch>();
  const { applicationQuestionnaire, applicationQuestionnaireLoading, applicationQuestionnaireError, questionnaireAnswersSaving } = useSelector(
    (state: RootState) => state.questionnaire
  );

  useEffect(() => {
    dispatch(fetchApplicationQuestionnaire(id));
  }, [id, dispatch]);

  const handleSave = () => {
    dispatch(saveQuestionnaireAnswers());
    toast.success(t('questionnaireSaved'));
  };

  const handleFinish = async () => {
    dispatch(finishQuestionnaireAnswers());
    await dispatch(fetchApplicationQuestionnaire(id));
    history.push('/');
    toast.success(t('questionnaireAnswered'));
  };

  const handleResend = async () => {
    dispatch(submitQuestionnaireAnswers());
    await dispatch(fetchApplicationQuestionnaire(id));
    history.push('/');
    toast.success(t('questionnaireAnswered'));
  };

  // Athuga hvort það sé búið að svara öllum spurningum (vildu ekki hafa það inni en gætu viljað seinna)
  // const canFinish = useMemo(() => Object.values(questionnaireAnswers).every((v) => v > 0), [questionnaireAnswers]);

  if (applicationQuestionnaireLoading === 'pending') {
    return (
      <>
        <FontAwesomeIcon spin icon={faSpinner} />
      </>
    );
  }

  if (applicationQuestionnaireError || applicationQuestionnaire == null || applicationQuestionnaire.questionnaire.status !== QuestionnaireStatusEnum.Published) {
    return <div>{t('common:internalError')}</div>;
  }

  return (
    <>
      <Card className="mb-2 text-center">
        <Card.Body>
          <h2>Raunfærnimat - {t('assessmentlist')}</h2>
          <h4>
            {t('industry')}: {applicationQuestionnaire.application.industry.name}
          </h4>
          <h4>
            {t('version')} {applicationQuestionnaire.questionnaire.version}
          </h4>
        </Card.Body>
      </Card>
      <Card className="mb-2">
        <Card.Body>
          <Row>
            <Col>
              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(applicationQuestionnaire.questionnaire.description || '') }} />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className="mb-2">
        <Card.Body>
          <Row>
            <Col>
              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(applicationQuestionnaire.questionnaire.instructions || '') }} />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {applicationQuestionnaire.interviewLocation && applicationQuestionnaire.interviewDateTime && (
        <Card className="mb-2">
          <Card.Body>
            <Card.Title>{t('interview')}</Card.Title>
            {applicationQuestionnaire.interviewLocation}, {dayjs(applicationQuestionnaire.interviewDateTime).format('DD.MM.YYYY HH:mm')}
          </Card.Body>
        </Card>
      )}
      {applicationQuestionnaire.status === 'PUBLISHED' && (
        <Card className="mb-2">
          <Card.Body>
            <Card.Title>{t('commentFromAssessment')}</Card.Title>
            {applicationQuestionnaire.assessments !== [] && (
              <>
                <div style={{ padding: '15px', marginTop: '5px', borderRadius: '5px' }}>
                  {applicationQuestionnaire.assessments.map((a) => (
                    <div key={a.id} dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.comments || '') }} />
                  ))}
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      )}
      <Card className="mb-2">
        <Card.Body>
          {applicationQuestionnaire.categories.length === 0 ? (
            <div>{t('common:noDataFound')}</div>
          ) : (
            <div>
              {applicationQuestionnaire.categories.map((category) => (
                <QuestionnaireDetailCategoryRow key={category.id} questionnaireId={applicationQuestionnaire.questionnaire.id} category={category} />
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
      <Card className="mb-2">
        <Card.Body className="text-right">
          {applicationQuestionnaire.status !== 'SENT' && (
            <>
              <Link to="/" className="btn btn-secondary">
                {t('common:close')}
              </Link>{' '}
            </>
          )}
          {applicationQuestionnaire.status === 'SENT' && (
            <>
              <Link to="/" className="btn btn-secondary">
                {t('common:cancel')}
              </Link>{' '}
              <OverlayTrigger placement="left" overlay={<Tooltip id="tooltip-other">{t('saveQuestionnaireAdFinishLater')}</Tooltip>}>
                <Button onClick={handleSave} disabled={questionnaireAnswersSaving}>
                  {questionnaireAnswersSaving && <FontAwesomeIcon icon={faSpinner} spin />} {t('saveQuestionnaire')}
                </Button>
              </OverlayTrigger>{' '}
              {applicationQuestionnaire.status === 'SENT' && (
                <OverlayTrigger placement="left" overlay={<Tooltip id="tooltip-other">{t('finishAndSendQuestionnaire')}</Tooltip>}>
                  <span>
                    <Button variant="success" onClick={handleFinish} disabled={questionnaireAnswersSaving}>
                      {questionnaireAnswersSaving && <FontAwesomeIcon icon={faSpinner} spin />} {t('finishQuestionnaire')}
                    </Button>
                    {/* <Button variant="success" onClick={handleFinish} disabled={questionnaireAnswersSaving || !canFinish} style={{ pointerEvents: !canFinish ? 'none' : undefined }}>
                  {questionnaireAnswersSaving && <FontAwesomeIcon icon={faSpinner} spin />} {t('finishQuestionnaire')}
                </Button> */}
                  </span>
                </OverlayTrigger>
              )}{' '}
            </>
          )}
          {applicationQuestionnaire.status === 'RETURNED' && (
            <OverlayTrigger placement="left" overlay={<Tooltip id="tooltip-other">{t('finishAndSendQuestionnaire')}</Tooltip>}>
              <span>
                <Button variant="success" onClick={handleResend} disabled={questionnaireAnswersSaving}>
                  {questionnaireAnswersSaving && <FontAwesomeIcon icon={faSpinner} spin />} {t('finishQuestionnaire')}
                </Button>
                {/* <Button variant="success" onClick={handleFinish} disabled={questionnaireAnswersSaving || !canFinish} style={{ pointerEvents: !canFinish ? 'none' : undefined }}>
                  {questionnaireAnswersSaving && <FontAwesomeIcon icon={faSpinner} spin />} {t('finishQuestionnaire')}
                </Button> */}
              </span>
            </OverlayTrigger>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default QuestionnaireDetail;
